import React from 'react';

import './index.scss';

import '@/theme/index.scss';

import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from 'styled-components';

import { sentryInit } from '@shared/utils';
import { SENTRY_ERROR_TYPES } from '@shared/constants';
import { EnigmaThemeProvider } from '@shared/components';

import { persistor, sagaMiddleware, store } from '@/redux/store/store';
import { theme } from '@/utils/theme';
import history from '@/utils/history';
import constants from '@/utils/constants';
import {
	generateBrowserFingerprint,
	getFingerprintCookie,
} from '@/utils/helper';
import themeConfig from '@/theme/themeConfig';
import rootSaga from '@/sagas';
import { ConfigProvider } from '@/widgets';

import App from './App';
import { initializeGarfieldWithTrackerID } from './utils/GarfieldUtils';
import { captureWebVitals } from './utils/webVitalsUtils';
import Vendor from './Vendor';
import { isVendorRoute } from './utils/Router.utils';

const {
	REACT_APP_SENTRY_DSN,
	BUILD_NUMBER,
	BUILD_ENVIRONMENT,
	OFFLINE_MERCHANT,
} = constants;

// Initiating Saga middleware
sagaMiddleware.run(rootSaga);

// Sentry configuration and initialization
sentryInit({
	dsn: REACT_APP_SENTRY_DSN,
	release: BUILD_NUMBER,
	environment: BUILD_ENVIRONMENT,
	sendClientReports: false,
	tracesSampleRate: 1.0,
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
	initialScope: {
		tags: { dashboard_type: OFFLINE_MERCHANT },
	},
	beforeSend: (event) => {
		if (!event?.tags?.errorType) {
			event.tags.errorType = SENTRY_ERROR_TYPES.Unhandled;
		}
		return event;
	},
});

captureWebVitals();

// Generate browser fingerprint on app init
if (!getFingerprintCookie()) {
	generateBrowserFingerprint();
}

// Initialising garfield with default tracker ID
initializeGarfieldWithTrackerID(constants.TRACKER_ID_OFFLINE_DASHBOARD);

const domNode = document.getElementById('root');
const root = createRoot(domNode);

export const MerchantApp = () => {
	return (
		<HelmetProvider>
			<Provider store={store} history={history}>
				<PersistGate persistor={persistor}>
					<EnigmaThemeProvider>
						<ConfigProvider theme={{ token: themeConfig.antdThemeConfig }}>
							<ThemeProvider theme={theme}>
								<App />
							</ThemeProvider>
						</ConfigProvider>
					</EnigmaThemeProvider>
				</PersistGate>
			</Provider>
		</HelmetProvider>
	);
};

export const VendorApp = () => {
	return (
		<EnigmaThemeProvider>
			<ThemeProvider theme={theme}>
				<Vendor />
			</ThemeProvider>
		</EnigmaThemeProvider>
	);
};

const isVendorKYCRoute = isVendorRoute();
const RootApp = isVendorKYCRoute ? VendorApp : MerchantApp;

const renderRoot = () => {
	root.render(<RootApp />);
};

renderRoot();

if (module.hot) {
	module.hot.accept('./App', renderRoot);
}
